import { PrimaryMenuItem } from '@design-systems/canvas-ui'
import {
  ChartLineIcon,
  CollectionIcon,
  DashboardIcon,
  PersonGroupIcon,
  PriceDollarIcon,
  ShipBoxIcon,
  WorkspaceIcon,
  TroubleshootIcon,
} from '@design-systems/react-icons'
import {
  PROD_TECH_ADMIN_ROLE,
  NON_PROD_TECH_ADMIN_ROLE,
  PROD_PRODUCT_TEAM_ADMIN_ROLE,
  NON_PROD_PRODUCT_TEAM_ADMIN_ROLE,
} from 'App/UserContext'
import { useLocation, useNavigate, matchPath } from 'react-router-dom'
import { useCallback, useContext, useMemo } from 'react'
import { UserContext } from 'App/UserContext'
import { useAuth } from '@praxis/component-auth'
import { ReactAppEnv } from 'apiConfig'
import { useEnv } from '@praxis/component-runtime-env'

interface FilterableNavItem {
  items?: FilterableNavItem[]
  isVisible?: boolean
  [key: string]: unknown
}

const filterChildrenItems = ({
  navItem,
}: {
  navItem: FilterableNavItem
}): FilterableNavItem | null => {
  const { items = [], isVisible = true, ...rest } = navItem
  if ((items as FilterableNavItem[]).length > 0) {
    const visibleItems = (items as FilterableNavItem[])
      .map((item) => filterChildrenItems({ navItem: item }))
      .filter((item): item is FilterableNavItem => item !== null)
    return visibleItems.length > 0 && isVisible
      ? {
          ...rest,
          items: visibleItems,
        }
      : null
  }
  return isVisible ? rest : null
}

const isPartnersOnline = () =>
  window.location.hostname.includes('partnersonline')

export const useFilteredNavigationItems = (): PrimaryMenuItem[] => {
  const navigate = useNavigate()
  const { environment }: { environment: ReactAppEnv } = useEnv()
  const { session } = useAuth()

  const techAdminRole =
    environment === 'prod' ? PROD_TECH_ADMIN_ROLE : NON_PROD_TECH_ADMIN_ROLE
  const productTeamAdminRole =
    environment === 'prod'
      ? PROD_PRODUCT_TEAM_ADMIN_ROLE
      : NON_PROD_PRODUCT_TEAM_ADMIN_ROLE
  const roles = session?.userInfo?.memberOf ?? []
  const isTechAdmin = roles.includes(techAdminRole)
  // Tech admin is implied to have all product team admin privileges as well
  const isProductTeamAdmin = roles.includes(productTeamAdminRole) || isTechAdmin

  const { pathname } = useLocation()
  const { isInternal, isPackagingSupplier, isFabricSupplier, isTrimSupplier } =
    useContext(UserContext)

  const isAnyLibrariesRouteActive = useCallback(() => {
    return (
      !!matchPath('/templateLibrary/*', pathname) ||
      !!matchPath('/measurements/*', pathname) ||
      !!matchPath('/productDetails/*', pathname) ||
      !!matchPath('/fileLibrary/*', pathname) ||
      !!matchPath('/materials/*', pathname) ||
      !!matchPath('/materials/artworks/*', pathname) ||
      !!matchPath('/materials/colors/*', pathname) ||
      !!matchPath('/materials/fabrics/*', pathname) ||
      !!matchPath('/materials/trims/*', pathname) ||
      !!matchPath('/materials/wash/*', pathname) ||
      !!matchPath('/combinations/*', pathname) ||
      !!matchPath('/palettes/*', pathname) ||
      pathname.startsWith('/negotiator/events/manage-')
    )
  }, [pathname])

  const isAnyToolsRouteActive = useCallback(() => {
    return (
      !!matchPath('/admin/*', pathname) || !!matchPath('/ui/admin/*', pathname)
    )
  }, [pathname])

  // Keep Canvas SideNav item identity stable so it does not reclaim focus on unrelated renders.
  const fullNavItems = useMemo(
    () => [
      {
        id: 'dashboard-navigation-item',
        label: 'Dashboard',
        icon: DashboardIcon,
        isActive: pathname === '/dashboard',
        onClick: () => navigate('/dashboard'),
      },
      {
        id: 'workspaces-navigation-item',
        label: 'Workspaces',
        icon: WorkspaceIcon,
        isActive: pathname === '/workspaces',
        isVisible: !isTrimSupplier && !isPackagingSupplier,
        onClick: () => navigate('/workspaces'),
      },
      {
        id: 'teams-navigation-item',
        label: 'Teams',
        icon: PersonGroupIcon,
        isVisible: !isPackagingSupplier,
        onClick: () => navigate('/teams'),
      },
      {
        id: 'negotiation-events-navigation-item',
        label: 'Negotiation events',
        icon: PriceDollarIcon,
        isActive: pathname.startsWith('/events'),
        onClick: () => {
          navigate('/events')
        },
      },
      {
        id: 'products-navigation-item',
        label: 'Products',
        icon: ShipBoxIcon,
        isActive: pathname === '/products',
        isVisible: !isFabricSupplier && !isTrimSupplier && !isPackagingSupplier,
        onClick: () => navigate('/products'),
      },
      {
        id: 'libraries-navigation-item',
        label: 'Libraries',
        icon: CollectionIcon,
        isActive: isAnyLibrariesRouteActive(),
        secondaryItems: [
          {
            id: 'product-assets-navigation-item',
            sectionHeader: 'Product assets',
            items: [
              {
                id: 'templates-navigation-item',
                label: 'Templates',
                isActive: !!matchPath('/templateLibrary/*', pathname),
                isVisible: !isPackagingSupplier,
                onClick: () => {
                  navigate('/templateLibrary/productTemplates')
                },
              },
              {
                id: 'measurements-navigation-item',
                label: 'Measurements',
                isActive: !!matchPath('/measurements/*', pathname),
                isVisible:
                  !isFabricSupplier && !isTrimSupplier && !isPackagingSupplier,
                onClick: () => {
                  navigate('/measurements')
                },
              },
              {
                id: 'details-navigation-item',
                label: 'Details',
                isActive: !!matchPath('/productDetails/*', pathname),
                isVisible:
                  !isFabricSupplier && !isTrimSupplier && !isPackagingSupplier,
                onClick: () => {
                  navigate('/productDetails')
                },
              },
              {
                id: 'packaging-navigation-item',
                label: 'Packaging',
                isActive: !!matchPath('/fileLibrary/*', pathname),
                onClick: () => {
                  navigate('/fileLibrary')
                },
              },
            ],
          },
          {
            id: 'material-assets-navigation-item',
            sectionHeader: 'Material assets',
            items: [
              {
                id: 'materials-navigation-item',
                label: 'All materials',
                isActive: !!matchPath('/materials/*', pathname),
                isVisible: !isPackagingSupplier,
                onClick: () => {
                  navigate('/materials')
                },
              },
              {
                id: 'artwork-navigation-item',
                label: 'Artwork',
                isActive: !!matchPath('/materials/artworks/*', pathname),
                isVisible: !isPackagingSupplier,
                onClick: () => {
                  navigate('/materials/artworks')
                },
              },
              {
                id: 'color-navigation-item',
                label: 'Color',
                isActive: !!matchPath('/materials/colors/*', pathname),
                isVisible: !isPackagingSupplier,
                onClick: () => {
                  navigate('/materials/colors')
                },
              },
              {
                id: 'fabric-navigation-item',
                label: 'Fabric',
                isActive: !!matchPath('/materials/fabrics/*', pathname),
                isVisible: !isPackagingSupplier,
                onClick: () => {
                  navigate('/materials/fabrics')
                },
              },
              {
                id: 'trim-navigation-item',
                label: 'Trim',
                isActive: !!matchPath('/materials/trims/*', pathname),
                isVisible: !isPackagingSupplier,
                onClick: () => {
                  navigate('/materials/trims')
                },
              },
              {
                id: 'wash-navigation-item',
                label: 'Wash',
                isActive: !!matchPath('/materials/wash/*', pathname),
                isVisible: !isPackagingSupplier,
                onClick: () => {
                  navigate('/materials/wash')
                },
              },
              {
                id: 'combinations-navigation-item',
                label: 'Combinations',
                isActive: !!matchPath('/combinations/*', pathname),
                isVisible: !isTrimSupplier && !isPackagingSupplier,
                onClick: () => {
                  navigate('/combinations')
                },
              },
              {
                id: 'palettes-navigation-item',
                label: 'Palettes',
                isActive: !!matchPath('/palettes/*', pathname),
                isVisible: !isPackagingSupplier,
                onClick: () => {
                  navigate('/palettes')
                },
              },
            ],
          },
          {
            id: 'costing-navigation-item',
            sectionHeader: 'Negotiation assets',
            items: [
              {
                id: 'negotiation-attributes-navigation-item',
                label: 'Attributes',
                isActive: pathname.endsWith('/libraries/manage-attributes'),
                isVisible: !isPartnersOnline(),
                onClick: () => {
                  navigate('/libraries/manage-attributes')
                },
              },
              {
                id: 'negotiation-templates-navigation-item',
                label: 'Templates',
                isActive: pathname.endsWith('/libraries/manage-templates'),
                isVisible: !isPartnersOnline(),
                onClick: () => {
                  navigate('/libraries/manage-templates')
                },
              },
            ],
          },
          {
            id: 'utilities-navigation-item',
            sectionHeader: 'Utilities',
            items: [
              {
                id: 'rfid-navigation-item',
                label: 'RFID tracking',
                isActive: !!matchPath('/rfid/*', pathname),
                isVisible: isInternal,
                onClick: () => {
                  navigate('/rfid')
                },
              },
            ],
          },
        ],
      },
      {
        id: 'reports-navigation-item',
        label: 'Reports',
        icon: ChartLineIcon,
        isActive: pathname === '/reporting',
        isVisible: !isPackagingSupplier,
        onClick: () => navigate('/reporting'),
      },
      {
        id: 'tools-navigation-item',
        label: 'Tools',
        icon: TroubleshootIcon,
        isActive: isAnyToolsRouteActive(),
        isVisible: isProductTeamAdmin,
        secondaryItems: [
          {
            id: 'administration-navigation-item',
            sectionHeader: 'Administration',
            items: [
              {
                id: 'administration-rules-item',
                label: 'Rules',
                isActive: !!matchPath('/admin/rules/*', pathname),
                isVisible: isProductTeamAdmin,
                onClick: () => {
                  navigate('/admin/rules')
                },
              },
              {
                id: 'administration-tags-item',
                label: 'Tags',
                isActive: !!matchPath('/admin/tags/*', pathname),
                isVisible: isProductTeamAdmin,
                onClick: () => {
                  navigate('/admin/tags')
                },
              },
              {
                id: 'administration-material-types-item',
                label: 'Material types',
                isActive: !!matchPath('/admin/materialTypes/*', pathname),
                isVisible: isProductTeamAdmin,
                onClick: () => {
                  navigate('/admin/materialTypes')
                },
              },
              {
                id: 'administration-vendor-merge-item',
                label: 'Vendor merge',
                isActive: !!matchPath('/admin/vendorMerge/*', pathname),
                isVisible: isProductTeamAdmin,
                onClick: () => {
                  navigate('/admin/vendorMerge')
                },
              },
              {
                id: 'administration-access-group-item',
                label: 'Access groups',
                isActive: !!matchPath('/admin/accessGroups/*', pathname),
                isVisible: isProductTeamAdmin,
                onClick: () => {
                  navigate('/admin/accessGroups')
                },
              },
              {
                id: 'system-events-navigation',
                label: 'System Events',
                isVisible: isProductTeamAdmin,
                isActive: !!matchPath(`/ui/admin/systemEvents/*`, pathname),
                onClick: () => navigate(`/ui/admin/systemEvents`),
              },
              {
                id: 'chat-assist-navigation',
                label: 'PLM Assist',
                isVisible: isProductTeamAdmin,
                isActive: !!matchPath(`/ui/admin/chat/*`, pathname),
                onClick: () => navigate(`/ui/admin/chat`),
              },
            ],
          },
          {
            id: 'dev-tools-navigation-item',
            sectionHeader: 'Dev Tools',
            isVisible: isTechAdmin,
            items: [
              {
                id: 'dev-tools-base-item',
                label: 'Home',
                isActive: !!matchPath('/ui/admin', pathname),
                isVisible: isTechAdmin,
                onClick: () => {
                  navigate('/ui/admin')
                },
              },
              {
                id: 'transcoding-navigation',
                label: 'Transcoding',
                isVisible: isTechAdmin,
                isActive: !!matchPath(`/ui/admin/transcoding/*`, pathname),
                onClick: () => navigate(`/ui/admin/transcoding`),
              },
              {
                id: 'bp-navigation',
                label: 'Business Partner',
                isVisible: isTechAdmin,
                isActive: !!matchPath(`/ui/admin/bp/*`, pathname),
                onClick: () => navigate(`/ui/admin/bp`),
              },
              {
                id: 'discussions-navigation',
                label: 'Discussions',
                isVisible: isTechAdmin,
                isActive: !!matchPath(`/ui/admin/discussions/*`, pathname),
                onClick: () => navigate(`/ui/admin/discussions`),
              },
              {
                id: 'resources-navigation',
                label: 'Resource Lookup',
                isVisible: isTechAdmin,
                isActive: !!matchPath(`/ui/admin/resources/*`, pathname),
                onClick: () => navigate(`/ui/admin/resources`),
              },
              {
                id: 'users-navigation',
                label: 'Users',
                isVisible: isTechAdmin,
                isActive: !!matchPath(`/ui/admin/users/*`, pathname),
                onClick: () => navigate(`/ui/admin/users`),
              },
              {
                id: 'reports-navigation',
                label: 'Reports',
                isVisible: isTechAdmin,
                isActive: !!matchPath(`/ui/admin/reports/*`, pathname),
                onClick: () => navigate(`/ui/admin/reports`),
              },
              {
                id: 'refresh-navigation',
                label: 'Data Refresh',
                isVisible: isTechAdmin,
                isActive: !!matchPath(`/ui/admin/refresh/*`, pathname),
                onClick: () => navigate(`/ui/admin/refresh`),
              },
              {
                id: 'redis-navigation',
                label: 'Redis Cache Tools',
                isVisible: isTechAdmin,
                isActive: !!matchPath(`/ui/admin/redis/*`, pathname),
                onClick: () => navigate(`/ui/admin/redis`),
              },
              {
                id: 'product-packet-navigation',
                label: 'Product Packet',
                isVisible: isTechAdmin,
                isActive: !!matchPath(`/ui/admin/productPacket/*`, pathname),
                onClick: () => navigate(`/ui/admin/productPacket`),
              },
            ],
          },
        ],
      },
    ],
    [
      isAnyLibrariesRouteActive,
      isAnyToolsRouteActive,
      isFabricSupplier,
      isInternal,
      isPackagingSupplier,
      isProductTeamAdmin,
      isTechAdmin,
      isTrimSupplier,
      navigate,
      pathname,
    ],
  )

  return useMemo(
    () =>
      fullNavItems
        .map((fullNavItem) => {
          const { isVisible = true, secondaryItems = [], ...rest } = fullNavItem
          if (secondaryItems && secondaryItems.length > 0) {
            const visibleSecondaryItems = secondaryItems
              .map((secondaryItem) =>
                filterChildrenItems({ navItem: secondaryItem }),
              )
              .filter(Boolean)
            return visibleSecondaryItems.length > 0 && isVisible
              ? {
                  ...rest,
                  secondaryItems: visibleSecondaryItems,
                }
              : null
          } else {
            return isVisible ? rest : null
          }
        })
        .filter(Boolean) as PrimaryMenuItem[],
    [fullNavItems],
  )
}
